// ---------------------------------------------------------------------------
// siteConstants.js
// Shared between SiteLayer.jsx and SiteIDPanel.jsx
// Single source of truth for asset type colors, labels, and icons.
// ---------------------------------------------------------------------------
 
export const ASSET_TYPE_COLORS = {
  // Anchors
  well_pad:               "#f59e0b",
  facility_site:          "#10b981",
  site:                   "#22c55e",
  fence:                  "#f97316",
  tank_battery:           "#3b82f6",
  // Components
  well_head:              "#06b6d4",
  well_head_boundary:     "#0891b2",
  storage_tank:           "#8b5cf6",
  storage_tank_boundary:  "#7c3aed",
  separator:              "#ec4899",
  separator_boundary:     "#db2777",
  flare:                  "#ef4444",
  valve:                  "#f59e0b",
  valve_boundary:         "#d97706",
  pipeline:               "#64748b",
  road:                   "#c528bd",
  fence_access:           "#84cc16",
  power_pole:             "#fbbf24",
  tank:                   "#60a5fa",
  // Context
  detected_well:          "#fb923c",
  rrc_well_surface:       "#94a3b8",
  rrc_well_lateral:       "#64748b",
  og_lease:               "#a78bfa",
  sr_lease:               "#818cf8",
  sr_easement:            "#6366f1",
  frac_pond:              "#34d399",
  spill_perimeter:        "#fca5a5",
  survey_mark:            "#e2e8f0",
  // Default
  _default:               "#9ca3af",
};
 
export function assetColor(assetType) {
  return ASSET_TYPE_COLORS[assetType] ?? ASSET_TYPE_COLORS._default;
}
 
// MapLibre match expression — generated from ASSET_TYPE_COLORS
// Import this into SiteLayer.jsx instead of defining inline.
export const FEATURE_TYPE_COLOR_EXPR = [
  "match", ["get", "feature_class"],
  ...Object.entries(ASSET_TYPE_COLORS)
    .filter(([k]) => k !== "_default")
    .flatMap(([k, v]) => [k, v]),
  ASSET_TYPE_COLORS._default,
];
 
export const ASSET_LABELS = {
  well_pad:               { label: "Well Pad",              icon: "⬡", group: "anchor"    },
  facility_site:          { label: "Facility",              icon: "⬢", group: "anchor"    },
  site:                   { label: "Site",                  icon: "⬡", group: "anchor"    },
  fence:                  { label: "Fence",                 icon: "⬟", group: "anchor"    },
  tank_battery:           { label: "Tank Battery",          icon: "⬡", group: "anchor"    },
  well_head:              { label: "Well Head",             icon: "◎", group: "component" },
  well_head_boundary:     { label: "Well Head Bndy",        icon: "○", group: "component" },
  storage_tank:           { label: "Storage Tank",          icon: "⊙", group: "component" },
  storage_tank_boundary:  { label: "Tank Boundary",         icon: "○", group: "component" },
  separator:              { label: "Separator",             icon: "◈", group: "component" },
  separator_boundary:     { label: "Separator Bndy",        icon: "○", group: "component" },
  flare:                  { label: "Flare",                 icon: "△", group: "component" },
  valve:                  { label: "Valve",                 icon: "◇", group: "component" },
  valve_boundary:         { label: "Valve Bndy",            icon: "○", group: "component" },
  pipeline:               { label: "Pipeline",              icon: "─", group: "component" },
  road:                   { label: "Road",                  icon: "═", group: "component" },
  fence_access:           { label: "Fence Access",          icon: "⊢", group: "component" },
  power_pole:             { label: "Power Pole",            icon: "✦", group: "component" },
  tank:                   { label: "Tank",                  icon: "⊙", group: "component" },
  detected_well:          { label: "Detected Well",         icon: "◉", group: "context"   },
  rrc_well_surface:       { label: "RRC Well",              icon: "●", group: "context"   },
  rrc_well_lateral:       { label: "RRC Lateral",           icon: "─", group: "context"   },
  frac_pond:              { label: "Frac Pond",             icon: "◓", group: "context"   },
  spill_perimeter:        { label: "Spill Perimeter",       icon: "⊘", group: "context"   },
  og_lease:               { label: "OG Lease",              icon: "▭", group: "context"   },
  sr_lease:               { label: "SR Lease",              icon: "▭", group: "context"   },
  sr_easement:            { label: "SR Easement",           icon: "▭", group: "context"   },
  survey_mark:            { label: "Survey Mark",           icon: "⊕", group: "context"   },
};
 
export function assetInfo(assetType) {
  return ASSET_LABELS[assetType] ?? {
    label: (assetType ?? "unknown")
      .replace(/_/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase()),
    icon:  "·",
    group: "context",
  };
}
 
export const SITE_TYPE_STYLES = {
  well_pad:        { color: "#f59e0b", label: "Well Pad"        },
  facility_site:   { color: "#10b981", label: "Facility"        },
  production_site: { color: "#22c55e", label: "Production"      },
  fenced_site:     { color: "#f97316", label: "Fenced"          },
  tank_battery:    { color: "#3b82f6", label: "Tank Battery"    },
  tank_site:       { color: "#6366f1", label: "Tank Site"       },
  candidate_site:  { color: "#6b7280", label: "Candidate"       },
};
 
export function siteTypeStyle(siteType) {
  return SITE_TYPE_STYLES[siteType] ?? { color: "#6b7280", label: siteType ?? "Unknown" };
}